/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import SqlImportModel, { SQLImportIdFieldName } from 'cio/models/sql-import';
import ApiService from 'cio/services/api';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import {
  BIG_QUERY_LOCATIONS,
  ImportType,
  isDataWarehouse,
  isQueryTimedOutError,
  SqlImportDatabase,
  SqlImportPreviewResponse,
} from 'cio/utils/sql-import';
import DS from 'ember-data';
import ObjectTypeModel from 'cio/models/object-type';
import SqlImportDatabaseModel from 'cio/models/sql-import-database';

interface SqlImportsCreateQueryArgs {
  model: SqlImportModel;
  runQuery?: boolean;
  readOnly?: boolean;
  onQuery?: ({
    hasSuccessfulQuery,
    hasSuccessfulRelationshipsQuery,
  }: {
    hasSuccessfulQuery: boolean;
    hasSuccessfulRelationshipsQuery?: boolean;
  }) => unknown;
  onSubmit: () => unknown;
  onCancel?: () => unknown;
  changeStep: () => unknown;
  isRelationshipQuery?: boolean;
}

interface SQLImportsPreviewData {
  query: string;
  id_field_name: SQLImportIdFieldName;
  database_id: string;
  import_id: string;
  location: string;
  import_type: ImportType & 'relationship';
  object_type_id?: number;
  entity1_type_id?: number;
  entity1_id_field_name?: string;
  entity2_type_id?: number;
  entity2_id_field_name?: string;
}

export default class SqlImportsCreateQuery extends Component<SqlImportsCreateQueryArgs> {
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;

  @tracked hasSuccessfulQuery = false;
  @tracked editorId?: string;

  get database() {
    return this.args.model.belongsTo('database').value() as SqlImportDatabaseModel | null;
  }

  get existingLines() {
    return this.args.model.query?.split('\n');
  }

  get mode() {
    if (this.database?.type === SqlImportDatabase.mysql) {
      return 'mysql';
    } else if (this.database?.type === SqlImportDatabase.postgres) {
      return 'pgsql';
    } else {
      return 'sql';
    }
  }

  get isDataWarehouse() {
    return isDataWarehouse(this.database?.type);
  }

  get showLocationDropdown() {
    return this.database?.type === SqlImportDatabase.bigQuery;
  }

  get locationOptions() {
    if (this.database?.type === SqlImportDatabase.bigQuery) {
      return BIG_QUERY_LOCATIONS;
    }

    return [];
  }

  get selectedObject() {
    return this.args.model.belongsTo('objectType').value() as ObjectTypeModel;
  }

  @computed('args.{model.location,readOnly}', 'showLocationDropdown', 'query', 'runQueryTask.isRunning')
  get runQueryDisabled() {
    if (this.showLocationDropdown && isNone(this.args.model.location)) {
      return true;
    }

    return (this.args.readOnly ?? false) || this.runQueryTask.isRunning || !this.query;
  }

  @computed('args.model.location', 'showLocationDropdown', 'query')
  get runQueryDisabledTooltip() {
    if (this.showLocationDropdown && isNone(this.args.model.location)) {
      return 'You must select a location to run the query in';
    }

    if (!this.query) {
      return 'Write a query to run it!';
    }

    return null;
  }

  @computed('runQueryTask.last.value')
  get isQueryTimedOutError() {
    let error = this.runQueryTask.last?.value?.error;

    return typeof error === 'string' && isQueryTimedOutError(error);
  }

  @computed('args.model.{idFieldName,query,objectType,relationship.entity1_id_field_name},args.isRelationshipQuery')
  get query() {
    let { model, isRelationshipQuery } = this.args;

    if (!isRelationshipQuery && model.query) {
      return model.query;
    }

    if (model.relationship?.query) {
      return model.relationship.query;
    }

    if (this.database?.type === SqlImportDatabase.snowflake) {
      return `SELECT ${model.idFieldName} AS "${model.idFieldName}", EMAIL AS "email", PHONE_NUMBER AS "phone_number"
FROM my_table
WHERE last_updated > {{last_sync_time}}
`;
    }

    if (isRelationshipQuery) {
      return `SELECT person_id AS ${model.relationship?.entity1_id_field_name ?? 'id'}, company_id AS ${
        model.relationship?.entity2_id_field_name ?? 'object_id'
      }
FROM my_table
WHERE last_updated > {{last_sync_time}}
`;
    }

    if (model.belongsTo('objectType').id()) {
      return `SELECT id AS ${model.idFieldName}
FROM my_table
WHERE last_updated > {{last_sync_time}}
`;
    }

    return `SELECT ${model.idFieldName}, email, first_name, primary_phone AS phone
FROM my_table
WHERE last_updated > {{last_sync_time}}
`;
  }

  @action
  didChangeQuery(query: string) {
    if (this.args.isRelationshipQuery) {
      this.args.model.set('relationship', {
        ...this.args.model.relationship,
        query,
      });
    } else {
      this.args.model.set('query', query);
    }
    this.hasSuccessfulQuery = false;
  }

  @action
  onInsert() {
    if (this.args.runQuery) {
      this.runQueryTask.perform();
    }

    if (this.args.model.get('isNew')) {
      this.analytics.track(
        'sql_imports',
        this.args.isRelationshipQuery ? 'write_relationships_query_started' : 'write_query_started',
        {
          model: this.args.model,
        },
      );
    }
  }

  @action
  onInitializeAce(editorId: string) {
    this.editorId = editorId;
  }

  @action
  updateLocation(selection: Record<string, string>) {
    this.args.model.set('location', selection.value);
  }

  @action
  previewTimeoutOverrideDismiss() {
    this.analytics.track('sql_imports', 'preview_timeout_dismiss', { model: this.args.model });

    this.runQueryTask.cancelAll({ resetState: true });
  }

  @action
  previewTimeoutOverrideContinue() {
    this.analytics.track('sql_imports', 'preview_timeout_continue', { model: this.args.model });

    this.runQueryTask.cancelAll({ resetState: true });
    this.args.onSubmit();

    this.handleQueryResponse(true);
  }

  runQueryTask = dropTask(async () => {
    this.analytics.track('sql_imports', 'run_query_started', { model: this.args.model });

    try {
      let response = await this.fetchPreviewTask.perform();

      this.trackErrors(response);
      this.handleResponse(response);

      return response;
    } catch (error) {
      this.apiErrors.report(error);
    }

    return null;
  });

  fetchPreviewTask = dropTask(async () => {
    let { model, isRelationshipQuery = false } = this.args;

    let data: SQLImportsPreviewData = {
      query: isRelationshipQuery && model.relationship ? model.relationship.query : model.query,
      id_field_name: model.idFieldName,
      database_id: model.belongsTo('database').id(),
      import_id: model.id,
      location: model.location,
      import_type: model.importType as SQLImportsPreviewData['import_type'],
    };

    if (isRelationshipQuery) {
      data.import_type = 'relationship' as SQLImportsPreviewData['import_type'];
      data.entity1_type_id = model.relationship?.entity1_type_id;
      data.entity1_id_field_name = model.relationship?.entity1_id_field_name;
      data.entity2_type_id = model.relationship?.entity2_type_id;
      data.entity2_id_field_name = model.relationship?.entity2_id_field_name;
    }

    if (model.importType === ImportType.Object) {
      data.object_type_id = Number(model.belongsTo('objectType').id());
    }

    return await this.api.post('sql_imports_preview', { data });
  });

  private trackErrors(response: SqlImportPreviewResponse) {
    if (response.error) {
      this.analytics.track('sql_imports', 'run_query_failed', { errorMessage: response.error, model: this.args.model });
    } else if (!isEmpty(response.validations?.errors)) {
      this.analytics.track('sql_imports', 'run_query_failed', {
        model: this.args.model,
        errorMessage: response.validations?.errors?.join(', '),
      });
    }
  }

  private handleResponse(response: SqlImportPreviewResponse) {
    let hasErrors = (response.validations?.errors?.length ?? 0) > 0 || response.error;

    this.handleQueryResponse(!hasErrors);
  }

  private handleQueryResponse(isSuccessful: boolean) {
    this.hasSuccessfulQuery = isSuccessful;
    this.args.onQuery?.({ hasSuccessfulQuery: this.hasSuccessfulQuery });
  }
}
